import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import "./styles/AttachmentsScheduler.scss";
import { toastSuccess } from "../../utils";
import AppContext from "../../context/appContext";
import SheetContext from "../../context/sheetContext";

import {
  initialStateDeleteFile,
  initialStateEditFile,
  initialStatePlanning,
  initialStateToForm,
} from "./initialStates";
import { deleteService, getService } from "../../services/axios/services";

import TableLoading from "../../components/TableLoading/TableLoading";
import LoadError from "../../components/LoadError/LoadError";
import ScriptBox from "../../components/ScriptBox/ScriptBox";
import CreateAndUpdateMaterialsPlanning from "./CreateAndUpdatePlannings/CreateAndUpdateMaterialsPlanning";
import EditIcon from "../../components/VectorIcons/EditIcon";
import AddMore from "../../components/AddMore/AddMore";
import UploadBox from "../../components/UploadBox/UploadBox";
import SimpleScriptBox from "../ProjectSheet/SimpleScriptBox/SimpleScriptBox";
import ConfirmationBox from "../../components/ConfirmationBox/ConfirmationBox";
import EditPlanningAttachment from "./EditPlanningAttachment/EditPlanningAttachment";
import { planningTypesByContentType } from "./schedulerModelByContentType";
import {
  returnAttachmentTypesListByContentType,
  returnNextValidEpisodeNumber,
  returnNextValidVersionNumber,
  buildDeleteAttachmentEndpoint,
  collectAllAttachmentIds,
  collectAddendumAssociations,
  computeFormUpdate,
} from "./utils";
import {
  toReleaseAttachment,
  buildPlanningReleaseGroups,
} from "./releaseFlow.utils";
import FileShareBox from "../ProjectSheet/FileShareBox/FileShareBox";
import UserContext from "../../context/userContext";
import { PlanningGrid } from "./PlanningGrid/PlanningGrid";
import { useAttachmentActions } from "../../shared/utils/useAttachmentsActions";
import {
  deletePlanningChapter,
  deletePlanningMaterial,
} from "../../services/api/attachments/attachments.api";
import {
  deletePlanningChapterAddendum,
  deletePlanningEpisodeAddendum,
} from "../../services/api/attachments/addendum.api";
import DeleteAddendumModal from "./DeleteAddendumModal/DeleteAddendumModal";
import ReleaseFlowContext from "../../context/releaseFlowContext";
import SingleReleaseTextsModal from "../ProjectSheet/ListFiles/SingleReleaseTextsModal/SingleReleaseTextsModal";

const AttachmentsScheduler = ({
  schedulerType,
  contentId,
  type = "projectSheet",
  downloadContext,
  contentType,
  customHeader,
  reload,
  sharingPermissions,
}) => {
  const appContext = useContext(AppContext);
  const projectSheetContext = useContext(SheetContext);
  const { hasPermission } = useContext(UserContext);
  const releaseFlowContext = useContext(ReleaseFlowContext);

  const [statePlanning, setStatePlanning] = useState(initialStatePlanning);
  const [stateReleaseIsOpen, setStateReleaseIsOpen] = useState(false);
  const [stateRelease, setStateRelease] = useState({ isOpen: false });
  const [stateError, setStateError] = useState(false);
  const [stateEditFile, setStateEditFile] = useState(initialStateEditFile);
  const [stateDeleteFile, setStateDeleteFile] = useState(
    initialStateDeleteFile
  );
  const [stateDeleteAddendum, setStateDeleteAddendum] = useState({
    open: false,
    scope: "chapter",
    attachmentId: null,
    associations: [],
  });
  const [actionType, setActionType] = useState(null);
  const [formData, setFormData] = useState(initialStateToForm());
  const [attachmentsTypesList, setAttachmentsTypesList] = useState(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [openShareBox, setOpenShareBox] = useState(false);
  const [openIds, setOpenIds] = useState([]);
  const toggleOpenId = useCallback((id) => {
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const selectedFileIds = useMemo(
    () => new Set(selectedFiles.map((f) => f.id)),
    [selectedFiles]
  );

  const {
    fetchAttachmentActions,
    canDelete,
    canReleaseChannel,
    canUnReleaseChannel,
    getDeleteReason,
    getDeleteUnavailableMessage,
    getReleaseUnavailableMessage,
    getUnReleaseUnavailableMessage,
    canDownload,
  } = useAttachmentActions();

  const PLANNING_CONTENT_DATA = planningTypesByContentType(schedulerType, type);

  const projectType =
    contentType?.toLowerCase() ??
    projectSheetContext.list.result.projectType.toLowerCase();

  const typeNames = {
    "Capítulos/Sinopse": "Blocos",
    Materiais: "Materiais",
  };

  const isChapter = schedulerType === "Capítulos/Sinopse";
  const isMaterial = schedulerType === "Materiais";

  const showForProjectSheet = !statePlanning.loading && type === "projectSheet";

  const noPlanningData =
    !statePlanning.loading &&
    !stateError &&
    (isChapter
      ? !statePlanning?.data?.blocks?.length &&
        !statePlanning?.data?.materials?.length
      : !statePlanning?.data?.materials?.length);

  const notLoadingAndHasContent =
    !statePlanning.loading && !noPlanningData && !!attachmentsTypesList;

  const createAndEditTypes = {
    create: {
      modalTitle: isMaterial ? "Criar Planejamento" : "Criar",
      icon: "",
    },
    edit: {
      modalTitle: isMaterial ? "Editar Planejamento" : "Editar",
      icon: <EditIcon color="#FFF" />,
    },
  };

  const handleCreate = () => {
    setFormData(
      initialStateToForm(isChapter ? { numberChaptersBlock: 6 } : {})
    );
    setActionType("create");
    appContext.setCurrentStepBox("CreateAndUpdateBlockPlanning");
  };

  const handleEdit = useCallback(
    (item, editType) => {
      setFormData(initialStateToForm({ ...item, editType: editType }));
      setActionType("edit");
      appContext.setCurrentStepBox("CreateAndUpdateBlockPlanning");
    },
    [appContext]
  );

  const handleEditFile = useCallback(
    (item) => {
      appContext.setCurrentStepBox("editChaptersOrEpisodes");
      setActionType("editAttachment");
      setStateEditFile({
        current: item,
      });
    },
    [appContext]
  );

  const resetCreateOrEdit = useCallback(() => {
    setFormData(initialStateToForm());
  }, []);

  const handleChangeByInputName = (value, inputName) => {
    setFormData((prev) =>
      computeFormUpdate(prev, inputName, value, statePlanning.data["materials"])
    );
  };

  const handleOpenReleaseTexts = useCallback(
    (currentAttachment, versions, releaseContext) => {
      const copyCurrent = { ...currentAttachment };

      let nextReleaseContext = releaseContext;

      if (releaseContext?.type === "planningAddendum") {
        const associations = collectAddendumAssociations(
          statePlanning.data,
          currentAttachment?.id
        );
        nextReleaseContext = { ...releaseContext, associations };
      }

      setStateReleaseIsOpen(true);
      setStateRelease({
        isOpen: true,
        current: copyCurrent,
        versions,
        releaseContext: nextReleaseContext,
      });
    },
    [statePlanning.data]
  );

  const handleDeleteAttachment = useCallback((attachment, parent) => {
    setStateDeleteFile({
      open: true,
      deleteType: "attachment",
      currentFile: {
        attachment,
        parent,
      },
    });
  }, []);

  const handleDeleteVersion = useCallback((materialVersion) => {
    setStateDeleteFile({
      open: true,
      deleteType: "materialVersion",
      currentFile: {
        materialVersion,
      },
    });
  }, []);

  const handleDeleteChapter = useCallback((chapter) => {
    setStateDeleteFile({
      open: true,
      deleteType: "chapter",
      currentFile: {
        chapter,
      },
    });
  }, []);

  const closeDeleteConfirmation = () => {
    setStateDeleteFile({
      currentFile: null,
      open: false,
      deleteType: null,
    });
  };

  const handleDeleteAddendum = useCallback(
    (addendum, scope = "chapter") => {
      const attachmentId = addendum?.attachment?.id ?? addendum?.attachmentId;

      const associations = collectAddendumAssociations(
        statePlanning.data,
        attachmentId,
        addendum
      );

      setStateDeleteAddendum({
        open: true,
        scope,
        attachmentId,
        associations,
      });
    },
    [statePlanning.data]
  );

  const closeDeleteAddendum = () => {
    setStateDeleteAddendum((prev) => ({ ...prev, open: false }));
  };

  const confirmDeleteAddendum = () => {
    const { scope, attachmentId } = stateDeleteAddendum;

    const deleteFn =
      scope === "episode"
        ? deletePlanningEpisodeAddendum
        : deletePlanningChapterAddendum;

    appContext.setShowGlassLoading(true);
    closeDeleteAddendum();

    deleteFn({ attachmentId })
      .then(() => {
        appContext.setShowGlassLoading(false);
        toastSuccess("Adendo removido com sucesso");
        getPlanning();
      })
      .catch(() => {
        appContext.setShowGlassLoading(false);
      });
  };

  const handleDeleteFile = (attachment, parent) => {
    appContext.setShowGlassLoading(true);

    const endpoint = buildDeleteAttachmentEndpoint(attachment, parent);

    deleteService({
      endpoint,
      api: "v2",
    })
      .then(() => {
        appContext.setShowGlassLoading(false);
        toastSuccess("Anexo removido com sucesso");
        getPlanning();
      })
      .catch(() => {
        appContext.setShowGlassLoading(false);
      });
  };

  const handleDeleteMaterialVersion = (materialVersion) => {
    appContext.setShowGlassLoading(true);

    deletePlanningMaterial({ materialId: materialVersion.id })
      .then(() => {
        appContext.setShowGlassLoading(false);
        toastSuccess("Versão removida com sucesso");
        getPlanning();
      })
      .catch(() => {
        appContext.setShowGlassLoading(false);
      });
  };

  const handleDeletePlanningChapter = (chapter) => {
    appContext.setShowGlassLoading(true);

    deletePlanningChapter({
      blockId: chapter.blockId,
      chapterId: chapter.id,
    })
      .then(() => {
        appContext.setShowGlassLoading(false);
        toastSuccess("Capítulo removido com sucesso");
        getPlanning();
      })
      .catch(() => {
        appContext.setShowGlassLoading(false);
      });
  };

  const getPlanning = useCallback(
    (flow) => {
      setStatePlanning((prevState) => ({ ...prevState, loading: true }));

      const endpoint = `Planning/contents/${contentId}`;

      getService({
        endpoint,
        api: "v2",
      })
        .then((response) => {
          if (!!response) {
            const episodeNumber = returnNextValidEpisodeNumber({
              versionsList: response["materials"],
            });

            setFormData((prevFormData) => {
              const shouldResetVersionNumberForEpisode =
                flow === "saveAndCreateNewFlow" &&
                actionType === "create" &&
                Number(prevFormData.typeId) === 1211;

              const shouldResetVersionOnNewCreate =
                flow === "createOnlyFlow" || shouldResetVersionNumberForEpisode;

              const versionNumber = returnNextValidVersionNumber({
                versionsList: response["materials"],
                typeId: Number(prevFormData.typeId),
              });

              return {
                ...initialStateToForm(),
                typeId: prevFormData.typeId,
                episodeNumber:
                  Number(prevFormData.typeId) === 1211 ? episodeNumber : 1,
                chapterNumber:
                  Number(prevFormData.typeId) === 1211 ? episodeNumber : null,
                version: shouldResetVersionOnNewCreate ? 1 : versionNumber,
              };
            });
          }

          response = {
            ...response,
            blocks: [...(response.blocks ?? [])].sort(
              (a, b) => (a.number ?? 0) - (b.number ?? 0)
            ),
          };

          setStatePlanning({
            loading: false,
            data: response,
          });

          const allAttachmentIds = collectAllAttachmentIds(response);

          const uniqueAttachmentIds = [
            ...new Set(allAttachmentIds.filter(Boolean)),
          ];

          if (uniqueAttachmentIds.length > 0) {
            fetchAttachmentActions(uniqueAttachmentIds);
          }
        })
        .catch((error) => {
          setStatePlanning((prevState) => ({ ...prevState, loading: false }));
          setStateError(true);
        });
    },
    [contentId, actionType, fetchAttachmentActions]
  );

  useEffect(() => {
    if (!statePlanning.data) getPlanning();
  }, [statePlanning.data, getPlanning]);

  useEffect(() => {
    if (!!appContext.currentOriginFileType.result && !attachmentsTypesList) {
    }
  }, [
    appContext.currentOriginFileType.result,
    projectType,
    attachmentsTypesList,
  ]);

  useEffect(() => {
    if (!attachmentsTypesList) {
      getService({
        endpoint: `${
          contentType === "Dramaturgia" ? "dramaturgies" : "varieties"
        }/thesaurus?origins=1`,
      }).then((res) => {
        setAttachmentsTypesList(
          returnAttachmentTypesListByContentType(projectType, res.data)
        );
      });
    }
    // eslint-disable-next-line
  }, []);

  const attachmentActions = useMemo(
    () => ({
      canDelete,
      canReleaseChannel,
      canUnReleaseChannel,
      getDeleteReason,
      getDeleteUnavailableMessage,
      getReleaseUnavailableMessage,
      getUnReleaseUnavailableMessage,
      canDownload,
    }),
    [
      canDelete,
      canReleaseChannel,
      canUnReleaseChannel,
      getDeleteReason,
      getDeleteUnavailableMessage,
      getReleaseUnavailableMessage,
      getUnReleaseUnavailableMessage,
      canDownload,
    ]
  );

  const handleSelectOrShare = useCallback(() => {
    if (isSelectionMode) {
      if (selectedFiles.length === 0) return;
      setOpenShareBox(true);
    } else {
      setIsSelectionMode(true);
    }
  }, [isSelectionMode, selectedFiles.length]);

  const handleCancelSelection = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedFiles([]);
  }, []);

  const handleOpenShareBox = useCallback(() => setOpenShareBox(true), []);

  console.log("selectedFiles:: ", selectedFiles);

  const handleOpenRelease = useCallback(() => {
    const normalizedAttachments = selectedFiles.map((file) =>
      toReleaseAttachment(file)
    );
    const groups = buildPlanningReleaseGroups(statePlanning.data);

    releaseFlowContext.openReleaseFlow({
      attachments: normalizedAttachments,
      contentId,
      reload: getPlanning,
      onSuccess: handleCancelSelection,
      groups,
    });
  }, [
    selectedFiles,
    statePlanning.data,
    releaseFlowContext,
    contentId,
    getPlanning,
    handleCancelSelection,
  ]);

  return (
    <div className="chaptersAndEpisodes">
      {(statePlanning.loading || !attachmentsTypesList) && <TableLoading />}

      {statePlanning?.data && showForProjectSheet && attachmentsTypesList && (
        <UploadBox
          title={PLANNING_CONTENT_DATA.messages.uploadMessage}
          projectId={contentId}
          listItems={statePlanning.data[PLANNING_CONTENT_DATA.dataKey] ?? []}
          materialsList={statePlanning.data?.materials ?? []}
          getPlanning={getPlanning}
          isChapter={isChapter}
          isMaterial={isMaterial}
          isSelectionMode={isSelectionMode}
          selectedFiles={selectedFiles}
          handleSelectOrShare={handleSelectOrShare}
          handleCancelSelection={handleCancelSelection}
          handleOpenShareBox={handleOpenShareBox}
          handleOpenRelease={handleOpenRelease}
        />
      )}

      {notLoadingAndHasContent && (
        <PlanningGrid
          contentId={contentId}
          planning={statePlanning}
          attachmentsTypesList={attachmentsTypesList}
          handleEdit={handleEdit}
          handleDeleteVersion={handleDeleteVersion}
          handleDeleteChapter={handleDeleteChapter}
          type={type}
          refresh={getPlanning}
          schedulerType={schedulerType}
          handleEditFile={handleEditFile}
          handleDeleteAttachment={handleDeleteAttachment}
          handleDeleteAddendum={handleDeleteAddendum}
          attachmentActions={attachmentActions}
          handleOpenReleaseTexts={handleOpenReleaseTexts}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          isSelectionMode={isSelectionMode}
          selectedFileIds={selectedFileIds}
          openIds={openIds}
          toggleOpenId={toggleOpenId}
        />
      )}

      {noPlanningData && (
        <div className="noPlanningCreated">
          {type === "channels"
            ? `Este projeto ainda não possui textos liberados do tipo ${typeNames[schedulerType]}`
            : `Nenhum planejamento criado para este conteúdo.`}
        </div>
      )}

      {!stateError &&
        showForProjectSheet &&
        !isSelectionMode &&
        hasPermission({ required: ["Planning"] }) && (
          <div onClick={handleCreate}>
            <AddMore text={PLANNING_CONTENT_DATA.messages.createMessage} />
          </div>
        )}

      {stateError && <LoadError />}

      {appContext.current.step === "CreateAndUpdateBlockPlanning" && (
        <ScriptBox
          open={appContext.current.step === "CreateAndUpdateBlockPlanning"}
          title={createAndEditTypes[actionType].modalTitle}
          icon={createAndEditTypes[actionType].icon}
          marginBottom={true}
          allowOverflow={true}
          removePadding={true}
          size="medium"
        >
          <CreateAndUpdateMaterialsPlanning
            action={actionType}
            planningData={PLANNING_CONTENT_DATA}
            list={statePlanning.data}
            contentId={contentId}
            getPlanning={getPlanning}
            schedulerType={schedulerType}
            handleChangeByInputName={handleChangeByInputName}
            stateCreateOrEdit={formData}
            resetCreateOrEdit={resetCreateOrEdit}
          />
        </ScriptBox>
      )}

      {appContext.current.step === "editChaptersOrEpisodes" && (
        <SimpleScriptBox
          open={appContext.current.step === "editChaptersOrEpisodes"}
          toggleScriptBox={() => appContext.setCurrentStepBox("")}
          title={stateEditFile.current.fileName}
          icon={<EditIcon color="#FFF" />}
          allowOverflow={true}
          size={"width-870"}
        >
          <EditPlanningAttachment
            file={stateEditFile.current}
            thesaurusFileType={true}
            darkBackgroundColor={false}
            listItems={statePlanning?.data}
            reload={getPlanning}
            isChapter={isChapter}
            isMaterial={isMaterial}
            typeValues={PLANNING_CONTENT_DATA}
            handleEditFile={handleEditFile}
            action={actionType}
          />
        </SimpleScriptBox>
      )}

      {stateReleaseIsOpen && (
        <SingleReleaseTextsModal
          releaseTexts={stateRelease}
          setStateOpen={setStateReleaseIsOpen}
          reload={getPlanning}
          projectType="Dramaturgia"
          submitCallback={() => setStateReleaseIsOpen(false)}
          showReleaseVersions={!isMaterial}
          releaseContext={stateRelease.releaseContext}
          contentId={contentId}
        />
      )}

      {stateDeleteFile.open && (
        <ConfirmationBox
          open={stateDeleteFile.open}
          textButton="Sim, excluir"
          closedBox={closeDeleteConfirmation}
          handleEvent={() =>
            stateDeleteFile.deleteType === "materialVersion"
              ? handleDeleteMaterialVersion(
                  stateDeleteFile.currentFile.materialVersion
                )
              : stateDeleteFile.deleteType === "chapter"
                ? handleDeletePlanningChapter(
                    stateDeleteFile.currentFile.chapter
                  )
                : handleDeleteFile(
                    stateDeleteFile.currentFile.attachment,
                    stateDeleteFile.currentFile.parent
                  )
          }
          title="Tem certeza?"
          text={
            stateDeleteFile.deleteType === "materialVersion"
              ? "Esta versão será excluída permanentemente do sistema e essa ação não poderá ser desfeita"
              : stateDeleteFile.deleteType === "chapter"
                ? "Este capítulo será excluído permanentemente do sistema e essa ação não poderá ser desfeita"
                : `Este ${PLANNING_CONTENT_DATA.schedulerType} será excluído permanentemente do sistema e essa ação não poderá ser desfeita`
          }
        />
      )}

      {stateDeleteAddendum.open && (
        <DeleteAddendumModal
          open={stateDeleteAddendum.open}
          scope={stateDeleteAddendum.scope}
          associations={stateDeleteAddendum.associations}
          onClose={closeDeleteAddendum}
          onConfirm={confirmDeleteAddendum}
        />
      )}

      {openShareBox && (
        <FileShareBox
          open={openShareBox}
          setOpen={setOpenShareBox}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          onClose={() => {
            setOpenShareBox(false);
          }}
          reload={reload}
          projectId={contentId}
        />
      )}
    </div>
  );
};

export default AttachmentsScheduler;
